import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Menu,
  ListItemIcon,
  Paper,
  Grid,
  alpha,
} from '@mui/material';
import {
  People as PeopleIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useToast } from '../ToastProvider/ToastProvider';
import { getUserPool } from '../../../lib/pools/pools';

interface Employee {
  userId: string;
  name: string;
  accessLevel: 'admin' | 'manager' | 'staff';
}

interface Schedule {
  scheduleId: string;
  userId: string;
  name: string;
  employees: string[];
}

interface ManageEmployeesModalProps {
  open: boolean;
  onClose: () => void;
  selectedSchedule: Schedule | null;
  employees: Employee[];
  onRemoveEmployee: (scheduleId: string, userId: Record<string, any>) => Promise<void>;
  onUpdateAccessLevel: (scheduleId: string, userId: string, accessLevel: string, name: string) => Promise<void>;
  onRefresh: (selectedEmployee: Employee, newAccessLevel?: string) => Promise<void>;
}

export default function ManageEmployeesModal({
  open,
  onClose,
  selectedSchedule,
  employees,
  onRemoveEmployee,
  onUpdateAccessLevel,
  onRefresh
}: ManageEmployeesModalProps) {
  const toast = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterAccessLevel, setFilterAccessLevel] = useState<string>('all');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showEditAccessDialog, setShowEditAccessDialog] = useState(false);
  const [newAccessLevel, setNewAccessLevel] = useState<string>('staff');
  const [isLoading, setIsLoading] = useState(false);

  // Filter employees based on search and access level
  const filteredEmployees = employees.filter(emp => {
    if (emp === undefined) {
      return false;
    }
    else {
      const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterAccessLevel === 'all' || emp.accessLevel === filterAccessLevel;
      return matchesSearch && matchesFilter;
    }
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, employee: Employee) => {
    setMenuAnchor(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditAccess = () => {
    if (selectedEmployee) {
      setNewAccessLevel(selectedEmployee.accessLevel);
      setShowEditAccessDialog(true);
      handleMenuClose();
    }
  };

  const handleRemoveClick = () => {
    setShowRemoveDialog(true);
    handleMenuClose();
  };

  const handleConfirmRemove = async () => {
    if (!selectedSchedule || !selectedEmployee) return;

    setIsLoading(true);
    try {
      await onRemoveEmployee(selectedSchedule.scheduleId, selectedEmployee);

      toast.showSuccess(`${selectedEmployee.name} has been removed from the schedule`);
      setShowRemoveDialog(false);
      setSelectedEmployee(null);
      await onRefresh(selectedEmployee);
    } catch (error) {
      toast.showError('Failed to remove employee. Please try again.');
      console.error('Error removing employee:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmAccessChange = async () => {
    if (!selectedSchedule || !selectedEmployee) return;

    if (newAccessLevel === selectedEmployee.accessLevel) {
      setShowEditAccessDialog(false);
      return;
    }

    setIsLoading(true);
    try {

      const currentUserId = getUserPool()?.getCurrentUser()?.getUsername();
      const currentUser = employees.find(e => e.userId === currentUserId);
      const priortyMap: Record<string, any> = {
        "admin": 3,
        "manager": 2,
        "staff": 1
      }
      if (priortyMap[newAccessLevel] <= priortyMap[currentUser?.accessLevel!]) {
        setNewAccessLevel(selectedEmployee.accessLevel);
        setShowEditAccessDialog(true);
        await onUpdateAccessLevel(selectedSchedule.scheduleId, selectedEmployee.userId, newAccessLevel, selectedEmployee.name);

        toast.showSuccess(`Access level updated for ${selectedEmployee.name}`);
        setShowEditAccessDialog(false);
        setSelectedEmployee(null);
        await onRefresh(selectedEmployee, newAccessLevel);
      } else {
        toast.showError('You do not have permission to change to this access level');
      }
    } catch (error) {
      toast.showError('Failed to update access level');
    } finally {
      setIsLoading(false);
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'admin':
        return 'error';
      case 'manager':
        return 'info';
      default:
        return 'primary';
    }
  };

  const getAccessLevelIcon = (level: string) => {
    switch (level) {
      case 'admin':
        return <AdminIcon fontSize="small" />;
      case 'manager':
        return <SecurityIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  const stats = {
    total: employees.length,
    admins: employees.filter(e => e?.accessLevel === 'admin').length,
    managers: employees.filter(e => e?.accessLevel === 'manager').length,
    employees: employees.filter(e => e?.accessLevel === 'staff').length
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <PeopleIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                Manage Staff
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedSchedule?.name} • {employees.length} staff member{employees.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Stats Cards */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats.total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#F44336', 0.05) }}>
                  <Typography variant="h4" fontWeight="bold" color="error">
                    {stats.admins}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Admins
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#FF9800', 0.05) }}>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {stats.managers}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Managers
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#2196F3', 0.05) }}>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.employees}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Staff
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Search and Filter */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ flex: 1, minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Access Level</InputLabel>
                <Select
                  value={filterAccessLevel}
                  onChange={(e) => setFilterAccessLevel(e.target.value)}
                  label="Access Level"
                >
                  <MenuItem value="all">All Levels</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="staff">Staff</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Employee List */}
            {filteredEmployees.length === 0 ? (
              <Alert severity="info">
                {searchQuery || filterAccessLevel !== 'all'
                  ? 'No staff match your search criteria'
                  : 'No staff in this schedule yet. Invite some team members to get started!'}
              </Alert>
            ) : (
              <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                {filteredEmployees.map((employee, index) => (
                  <React.Fragment key={employee.userId}>
                    <ListItem
                      secondaryAction={
                        <IconButton
                          edge="end"
                          disabled={employee.userId === selectedSchedule?.userId || employee.userId === getUserPool()?.getCurrentUser()?.getUsername()}
                          onClick={(e) => handleMenuOpen(e, employee)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      }
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        bgcolor: employee.userId === getUserPool()?.getCurrentUser()?.getUsername() ? alpha('#2196F3', 0.1) : "transparent"
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body1" fontWeight={600}>
                              {employee.name} {employee.userId === getUserPool()?.getCurrentUser()?.getUsername() ? "(Me)" : employee.userId === selectedSchedule?.userId ? "(Owner)" : ""}
                            </Typography>
                            <Chip
                              label={employee.accessLevel}
                              size="small"
                              color={getAccessLevelColor(employee.accessLevel)}
                              icon={getAccessLevelIcon(employee.accessLevel)}
                              sx={{ height: 20, fontSize: '0.7rem', color: 'white !important' }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredEmployees.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditAccess}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Access Level</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleRemoveClick} sx={{ color: 'error.main' }} disabled={getUserPool()?.getCurrentUser()?.getUsername() === selectedEmployee?.userId}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Remove from Schedule</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Access Level Dialog */}
      <Dialog open={showEditAccessDialog} onClose={() => setShowEditAccessDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'error.light' }}>
              <WarningIcon />
            </Avatar>
            <Typography variant="h6" fontWeight="bold">
              Change Access Level
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {selectedEmployee.name}
                </Typography>
                <Typography variant="caption">
                  Current level: <strong>{selectedEmployee.accessLevel}</strong>
                </Typography>
              </Alert>

              <FormControl fullWidth>
                <InputLabel>New Access Level</InputLabel>
                <Select
                  value={newAccessLevel}
                  onChange={(e) => setNewAccessLevel(e.target.value)}
                  label="New Access Level"
                >
                  <MenuItem value="staff">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">Staff</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Can view and manage own shifts
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="manager">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">Manager</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Can manage shifts and staff
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AdminIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">Admin</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Full schedule management access
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Changes take effect immediately and will update the staff member's permissions for this schedule.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={() => setShowEditAccessDialog(false)} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAccessChange}
            variant="contained"
            disabled={isLoading || newAccessLevel === selectedEmployee?.accessLevel}
          >
            {isLoading ? 'Updating...' : 'Update Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Employee Dialog */}
      <Dialog open={showRemoveDialog} onClose={() => setShowRemoveDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'error.light' }}>
              <WarningIcon />
            </Avatar>
            <Typography variant="h6" fontWeight="bold">
              Remove Staff Member
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Are you sure you want to remove this staff member?
                </Typography>
                <Typography variant="caption">
                  The staff member will lose access to this schedule and data pertaining to them removed.
                  They will have to be re-invited.
                </Typography>
              </Alert>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {selectedEmployee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedEmployee.name}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={() => setShowRemoveDialog(false)} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            variant="contained"
            color="error"
            disabled={isLoading}
            startIcon={<DeleteIcon />}
          >
            {isLoading ? 'Removing...' : 'Remove Staff'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}